import { apiClient } from '../lib/apiClient'

export interface TimesheetEntryV2 {
  id: string
  org_id: string
  resource_id: string
  user_id?: string
  project_id: string
  task_id?: string
  entry_date: string
  minutes_worked: number
  description?: string
  billable?: boolean
  rounded_billable_minutes?: number
  status?: 'draft' | 'submitted' | 'approved' | 'rejected'
  submitted_at?: string
  submitted_by?: string
  approved_by?: string
  approved_at?: string
  rejected_reason?: string
  created_at?: string
  updated_at?: string
  version?: number
}

export interface TimesheetEntryUI {
  id: string
  org_id: string
  resource_id: string
  user_id?: string
  project_id: string
  task_id?: string
  entry_date: string
  hours: number
  description?: string
  is_billable: boolean
  status: 'draft' | 'submitted' | 'approved' | 'rejected'
  created_at: string
  updated_at: string
}

export interface TimesheetPeriod {
  id: string
  org_id: string
  start_date: string
  end_date: string
  status: 'open' | 'submitted' | 'approved' | 'rejected'
  submitted_by?: string
  submitted_at?: string
  approved_by?: string
  approved_at?: string
  rejected_reason?: string
  created_at: string
}

export interface PeriodHistoryItem {
  id: string
  org_id: string
  period_start: string
  period_end: string
  status: 'open' | 'submitted' | 'approved' | 'rejected'
  submitted_by: string | null
  submitted_at: string | null
  approved_by: string | null
  approved_at: string | null
  rejected_reason: string | null
  created_at: string
  submitter_name: string | null
  submitter_email: string | null
  approver_name: string | null
  total_hours: number
}

export interface NotSubmittedItem {
  resource_id: string
  resource_name: string
  resource_email: string
  period_id: string
  period_start: string
  period_end: string
  draft_hours: number
  has_entries: boolean
}

export interface TimesheetApprovalWorkflow {
  id: string
  timesheet_period_id: string
  user_id: string
  submitted_at?: string
  submitted_by?: string
  approved_at?: string
  approved_by?: string
  rejected_at?: string
  rejected_by?: string
  rejection_reason?: string
  status: 'pending' | 'submitted' | 'approved' | 'rejected'
  created_at: string
  updated_at: string
}

export interface WeeklyTimesheetEntry {
  project_id: string
  task_id?: string
  monday?: number
  tuesday?: number
  wednesday?: number
  thursday?: number
  friday?: number
  saturday?: number
  sunday?: number
  is_billable: boolean
  description?: string
  monday_comment?: string
  tuesday_comment?: string
  wednesday_comment?: string
  thursday_comment?: string
  friday_comment?: string
  saturday_comment?: string
  sunday_comment?: string
}

const hoursToMinutes = (hours: number): number => Math.round(hours * 60)
const minutesToHours = (minutes: number): number => minutes / 60

const dbToUI = (entry: TimesheetEntryV2): TimesheetEntryUI => ({
  id: entry.id,
  org_id: entry.org_id,
  resource_id: entry.resource_id,
  user_id: entry.user_id,
  project_id: entry.project_id,
  task_id: entry.task_id,
  entry_date: entry.entry_date,
  hours: minutesToHours(entry.minutes_worked),
  description: entry.description,
  is_billable: entry.billable ?? true,
  status: entry.status || 'draft',
  created_at: entry.created_at || new Date().toISOString(),
  updated_at: entry.updated_at || new Date().toISOString()
})

export const timesheetService = {
  async getTimesheetEntries(userId: string, startDate: string, endDate: string): Promise<TimesheetEntryUI[]> {
    const data = await apiClient.get<TimesheetEntryV2[]>(
      `/timesheet-entries/v2?userId=${userId}&startDate=${startDate}&endDate=${endDate}`
    )
    return data.map(dbToUI)
  },

  async getTimesheetEntriesByResource(resourceId: string, startDate: string, endDate: string): Promise<TimesheetEntryUI[]> {
    const data = await apiClient.get<TimesheetEntryV2[]>(
      `/timesheet-entries/v2?resourceId=${resourceId}&startDate=${startDate}&endDate=${endDate}`
    )
    return data.map(dbToUI)
  },

  async getProjectTimesheetEntries(projectId: string, startDate: string, endDate: string): Promise<TimesheetEntryUI[]> {
    const data = await apiClient.get<TimesheetEntryV2[]>(
      `/timesheet-entries/v2?projectId=${projectId}&startDate=${startDate}&endDate=${endDate}`
    )
    return data.map(dbToUI)
  },

  async createTimesheetEntry(entry: Omit<TimesheetEntryUI, 'id' | 'created_at' | 'updated_at'>): Promise<TimesheetEntryUI> {
    if (entry.hours % 0.25 !== 0) {
      throw new Error('Hours must be in 15-minute increments (0.25, 0.5, 0.75, 1.0, etc.)')
    }
    const dbEntry = {
      org_id: entry.org_id,
      resource_id: entry.resource_id,
      user_id: entry.user_id,
      project_id: entry.project_id,
      task_id: entry.task_id,
      entry_date: entry.entry_date,
      minutes_worked: hoursToMinutes(entry.hours),
      description: entry.description,
      billable: entry.is_billable,
      status: entry.status,
    }
    const data = await apiClient.post<TimesheetEntryV2>('/timesheet-entries/v2', dbEntry)
    return dbToUI(data)
  },

  async updateTimesheetEntry(entryId: string, updates: Partial<TimesheetEntryUI>): Promise<TimesheetEntryUI> {
    if (updates.hours !== undefined && updates.hours % 0.25 !== 0) {
      throw new Error('Hours must be in 15-minute increments (0.25, 0.5, 0.75, 1.0, etc.)')
    }
    const dbUpdates: Partial<TimesheetEntryV2> = {}
    if (updates.hours !== undefined) dbUpdates.minutes_worked = hoursToMinutes(updates.hours)
    if (updates.is_billable !== undefined) dbUpdates.billable = updates.is_billable
    if (updates.description !== undefined) dbUpdates.description = updates.description
    if (updates.status !== undefined) dbUpdates.status = updates.status
    if (updates.project_id !== undefined) dbUpdates.project_id = updates.project_id
    if (updates.task_id !== undefined) dbUpdates.task_id = updates.task_id

    const data = await apiClient.patch<TimesheetEntryV2>(`/timesheet-entries/v2/${entryId}`, dbUpdates)
    return dbToUI(data)
  },

  async deleteTimesheetEntry(entryId: string): Promise<void> {
    return apiClient.delete(`/timesheet-entries/v2/${entryId}`)
  },

  async bulkUpsertWeeklyEntries(
    userId: string,
    resourceId: string,
    organizationId: string,
    weekStartDate: string,
    entries: WeeklyTimesheetEntry[]
  ): Promise<void> {
    const weekDates = this.getWeekDates(weekStartDate)
    const allEntries: Omit<TimesheetEntryV2, 'id' | 'created_at' | 'updated_at' | 'version'>[] = []

    entries.forEach(entry => {
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
      days.forEach((day, index) => {
        const hours = entry[day as keyof WeeklyTimesheetEntry] as number
        if (hours > 0) {
          const minutes = hoursToMinutes(hours)
          const commentKey = `${day}_comment` as keyof WeeklyTimesheetEntry
          const dayComment = entry[commentKey] as string | undefined
          allEntries.push({
            org_id: organizationId,
            resource_id: resourceId,
            user_id: userId,
            project_id: entry.project_id,
            task_id: entry.task_id,
            entry_date: weekDates[index],
            minutes_worked: minutes,
            billable: entry.is_billable,
            description: dayComment || entry.description,
            status: 'draft',
          })
        }
      })
    })

    await apiClient.post(`/timesheet-entries/v2/bulk-upsert`, {
      userId,
      weekStart: weekDates[0],
      weekEnd: weekDates[6],
      entries: allEntries,
    })
  },

  async bulkUpsertWeeklyEntriesForResource(
    resourceId: string,
    organizationId: string,
    weekStartDate: string,
    entries: WeeklyTimesheetEntry[]
  ): Promise<void> {
    const weekDates = this.getWeekDates(weekStartDate)
    const allEntries: Omit<TimesheetEntryV2, 'id' | 'created_at' | 'updated_at' | 'version'>[] = []

    entries.forEach(entry => {
      const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
      days.forEach((day, index) => {
        const hours = entry[day as keyof WeeklyTimesheetEntry] as number
        if (hours > 0) {
          const minutes = hoursToMinutes(hours)
          const commentKey = `${day}_comment` as keyof WeeklyTimesheetEntry
          const dayComment = entry[commentKey] as string | undefined
          allEntries.push({
            org_id: organizationId,
            resource_id: resourceId,
            user_id: undefined,
            project_id: entry.project_id,
            task_id: entry.task_id,
            entry_date: weekDates[index],
            minutes_worked: minutes,
            billable: entry.is_billable,
            description: dayComment || entry.description,
            status: 'draft',
          })
        }
      })
    })

    await apiClient.post(`/timesheet-entries/v2/bulk-upsert`, {
      weekStart: weekDates[0],
      weekEnd: weekDates[6],
      entries: allEntries,
    })
  },

  async getTimesheetPeriods(organizationId: string): Promise<TimesheetPeriod[]> {
    return apiClient.get<TimesheetPeriod[]>(`/orgs/${organizationId}/timesheet-periods`)
  },

  async getCurrentPeriod(organizationId: string, date: string): Promise<TimesheetPeriod | null> {
    return apiClient.get<TimesheetPeriod | null>(
      `/orgs/${organizationId}/timesheet-periods/current?date=${date}`
    )
  },

  async submitTimesheetForPeriod(userId: string, periodId: string): Promise<void> {
    return apiClient.post(`/timesheet-periods/${periodId}/submit`, { userId })
  },

  async submitTimesheetOnBehalf(periodId: string, resourceId: string): Promise<void> {
    return apiClient.post(`/timesheet-periods/${periodId}/submit`, { resourceId })
  },

  async getCurrentPeriodForResource(
    organizationId: string,
    resourceId: string,
    date: string
  ): Promise<TimesheetPeriod | null> {
    return apiClient.get<TimesheetPeriod | null>(
      `/orgs/${organizationId}/timesheet-periods/current-for-resource?resourceId=${resourceId}&date=${date}`
    )
  },

  async approveTimesheet(periodId: string, userId: string, approverId: string): Promise<void> {
    return apiClient.post(`/timesheet-periods/${periodId}/approve`, { userId, approverId })
  },

  async rejectTimesheet(periodId: string, userId: string, rejectorId: string, reason: string): Promise<void> {
    return apiClient.post(`/timesheet-periods/${periodId}/reject`, { userId, rejectorId, reason })
  },

  async getPendingApprovals(organizationId: string): Promise<TimesheetApprovalWorkflow[]> {
    return apiClient.get<TimesheetApprovalWorkflow[]>(
      `/orgs/${organizationId}/timesheet-approvals/pending`
    )
  },

  getWeekDates(startDate: string): string[] {
    // Parse as local date components to avoid UTC-midnight-to-previous-day shift
    const [y, m, d] = startDate.split('-').map(Number)
    const dates: string[] = []
    for (let i = 0; i < 7; i++) {
      const date = new Date(y, m - 1, d + i) // local time, no timezone shift
      const yy = date.getFullYear()
      const mm = String(date.getMonth() + 1).padStart(2, '0')
      const dd = String(date.getDate()).padStart(2, '0')
      dates.push(`${yy}-${mm}-${dd}`)
    }
    return dates
  },

  getWeekStartDate(date: Date): string {
    const day = date.getDay() // local day-of-week
    const diff = date.getDate() - day + (day === 0 ? -6 : 1) // ISO Monday
    const d = new Date(date.getFullYear(), date.getMonth(), diff)
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const dd = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${dd}`
  },

  validateHours(hours: number): boolean {
    return hours >= 0 && hours <= 24 && hours % 0.25 === 0
  },

  async getOrgTimesheetEntriesV2(orgId: string): Promise<TimesheetEntryUI[]> {
    const data = await apiClient.get<TimesheetEntryV2[]>(`/orgs/${orgId}/timesheet-entries/v2`)
    return data.map(dbToUI)
  },

  async getResourceForUser(_userId: string, orgId: string): Promise<any | null> {
    return apiClient.get<any>(`/orgs/${orgId}/resources/for-current-user`)
      .catch(() => null)
  },

  async getTimesheetPeriodHistory(
    orgId: string,
    filters?: { userId?: string; status?: string; startDate?: string; endDate?: string }
  ): Promise<PeriodHistoryItem[]> {
    const params = new URLSearchParams()
    if (filters?.userId) params.set('userId', filters.userId)
    if (filters?.status) params.set('status', filters.status)
    if (filters?.startDate) params.set('startDate', filters.startDate)
    if (filters?.endDate) params.set('endDate', filters.endDate)
    const qs = params.toString()
    return apiClient.get<PeriodHistoryItem[]>(
      `/orgs/${orgId}/timesheet-periods/history${qs ? `?${qs}` : ''}`
    )
  },

  async getNotSubmittedTimesheets(
    orgId: string,
    filters?: { startDate?: string; endDate?: string }
  ): Promise<NotSubmittedItem[]> {
    const params = new URLSearchParams()
    if (filters?.startDate) params.set('startDate', filters.startDate)
    if (filters?.endDate) params.set('endDate', filters.endDate)
    const qs = params.toString()
    return apiClient.get<NotSubmittedItem[]>(
      `/orgs/${orgId}/timesheet-periods/not-submitted${qs ? `?${qs}` : ''}`
    )
  },

  async reopenTimesheetPeriod(periodId: string): Promise<void> {
    return apiClient.post(`/timesheet-periods/${periodId}/reopen`, {})
  },
}
